/**
 * `useFullOrganization` — live read of the signed-in user's primary
 * org doc with EVERY field exposed.
 *
 * Distinct from `useOrgSubscription` (which only surfaces id + name +
 * subscription) because the Profile editor needs the long tail of
 * studio detail fields (address, GSTIN, banking, social, etc.).
 *
 * Same Firestore path either way — `organizations/{orgId}` — and
 * Firestore deduplicates the underlying snapshot listeners when the
 * same doc is subscribed from multiple hooks in the same tab. So
 * there's no perf cost to having two hooks read the same doc.
 *
 * Returns the raw doc data shape (typed loosely as Record<string,
 * unknown>) plus the id. The Profile page narrows the type with a
 * local cast — there's no formal schema for the org doc because
 * every studio fills in whatever fields they care about.
 */
import { doc, onSnapshot } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { db } from '@/lib/firebase';

export type FullOrg = {
  /** Firestore document id. */
  id: string;
  /** All fields from `organizations/{orgId}`. Profile page casts to
   *  the field-specific shape it needs. */
  data: Record<string, unknown>;
};

export type UseFullOrganizationResult = {
  /** Resolved org doc, or null when the user has no primary org. */
  org: FullOrg | null;
  loading: boolean;
  error: string | null;
};

export function useFullOrganization(): UseFullOrganizationResult {
  const { user, loading: authLoading } = useAuth();
  const [org, setOrg] = useState<FullOrg | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setOrg(null);
      setLoading(false);
      return;
    }

    let orgUnsub: (() => void) | null = null;

    const userUnsub = onSnapshot(
      doc(db, 'users', user.uid),
      (userSnap) => {
        const userData = userSnap.data();
        const primaryOrgId =
          typeof userData?.primaryOrgId === 'string'
            ? userData.primaryOrgId
            : null;
        if (!primaryOrgId) {
          setOrg(null);
          setLoading(false);
          return;
        }
        if (orgUnsub) orgUnsub();
        orgUnsub = onSnapshot(
          doc(db, 'organizations', primaryOrgId),
          (orgSnap) => {
            const data = orgSnap.data();
            if (!data) {
              setOrg(null);
            } else {
              setOrg({ id: primaryOrgId, data });
            }
            setLoading(false);
          },
          (err) => {
            setError(err.message);
            setLoading(false);
          },
        );
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );

    return () => {
      userUnsub();
      if (orgUnsub) orgUnsub();
    };
  }, [user, authLoading]);

  return { org, loading, error };
}
